<template>
  <div
    class="absolute inset-0"
    role="status"
    aria-busy="true"
    :aria-label="feedback.headline"
  >
    <IdleChromeShell :compact-layout="false" :hibernate="false">
      <div
        v-if="feedback.showStatus"
        class="flex flex-col items-center gap-[1.2vh] text-center"
        style="max-width: min(80vw, 28rem);"
      >
        <PlayerIdleStatusLine>{{ feedback.headline }}</PlayerIdleStatusLine>
        <p
          v-if="feedback.detail"
          class="m-0 text-white/25 tracking-wide"
          style="font-size: clamp(0.5rem, 0.75vw, 0.75rem); line-height: 1.45;"
        >
          {{ feedback.detail }}
        </p>
        <div
          v-if="showProgressTrack"
          class="mt-[1vh] w-full"
          style="width: clamp(8rem, 25vw, 22rem); max-width: min(80vw, 90vh);"
        >
          <div class="w-full h-[2px] rounded-full bg-white/10 overflow-hidden">
            <div
              v-if="feedback.indeterminateProgress"
              class="playback-surface-progress-indeterminate h-full rounded-full bg-white/50"
            />
            <div
              v-else-if="downloadPercentage != null"
              class="h-full rounded-full bg-white/50 transition-all duration-300 ease-out"
              :style="{ width: `${downloadPercentage}%` }"
            />
          </div>
        </div>
      </div>
    </IdleChromeShell>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, toRefs } from 'vue'
import IdleChromeShell from '@/components/IdleChromeShell.vue'
import PlayerIdleStatusLine from '@/components/PlayerIdleStatusLine.vue'
import usePlayer from '@/composables/usePlayer'
import { resolvePlaybackSurfaceLoadingFeedback } from '@/helpers/resolvePlaybackSurfaceLoadingFeedback'

const { state } = usePlayer()
const { downloadProgress, loadingCampaign } = toRefs(state)

const emptyForMs = ref(0)
let startedAt = 0
let tickTimer: ReturnType<typeof setInterval> | null = null

const feedback = computed(() => {
  const progressRaw = loadingCampaign.value ? downloadProgress.value : Number.NaN
  const downloadProgress01 = Number.isFinite(progressRaw) ? progressRaw : null
  return resolvePlaybackSurfaceLoadingFeedback({
    emptyForMs: emptyForMs.value,
    downloadProgress01
  })
})

/** Determinate % only after the first non-zero progress event (avoids stuck 0% bar). */
const downloadPercentage = computed(() => {
  if (!loadingCampaign.value || Number.isNaN(downloadProgress.value)) {
    return null
  }
  if (downloadProgress.value <= 0 || downloadProgress.value >= 1) {
    return null
  }
  return Math.min(99, Math.max(1, Math.round(downloadProgress.value * 100)))
})

const showProgressTrack = computed(() =>
  feedback.value.indeterminateProgress || downloadPercentage.value != null
)

onMounted(() => {
  startedAt = performance.now()
  emptyForMs.value = 0
  tickTimer = setInterval(() => {
    emptyForMs.value = performance.now() - startedAt
  }, 250)
})

onBeforeUnmount(() => {
  if (tickTimer != null) {
    clearInterval(tickTimer)
    tickTimer = null
  }
})
</script>

<style scoped>
@keyframes playback-surface-progress-indeterminate {
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(320%);
  }
}

.playback-surface-progress-indeterminate {
  width: 35%;
  animation: playback-surface-progress-indeterminate 1.35s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .playback-surface-progress-indeterminate {
    width: 100%;
    opacity: 0.45;
    animation: none;
  }
}
</style>
